/** Admin API methods for integration settings: registry connections and TLD channels
 * (subsystems/platform/facade/adapters/admin-api/src/admin/integrations.rs) and provider settings for VNNIC Registrar API,
 * eKYC, HSTM and gTLD reporting (subsystems/platform/facade/adapters/admin-api/src/admin/provider_settings.rs).
 *
 * Wire types mirror docs/integration-settings-api.md (ADR-016) section by section. Secrets are
 * write-only: no type here can carry a secret value back from the server (`SecretStatus` only
 * says whether one is configured, where it is kept and who changed it). */

import type { CallOptions, ISODateTime, ItemList, WriteOptions } from '../types';

// ---------------------------------------------------------------------------
// §1.3 Shared types

/** The deployment's environment (ADR-018 "one environment per deployment"): OTE on the OT&E
 * server (and in development), PRODUCTION on the production server. A label only: nothing in
 * the API selects an environment, every connection / provider / TLD has one configuration. */
export type IntegrationEnvironment = 'OTE' | 'PRODUCTION';
export type ConnectionKind = 'REGISTRY' | 'UPSTREAM_REGISTRAR';
export type ConnectionProtocol = 'EPP' | 'REST';
export type TldChannel = 'DIRECT_EPP' | 'REGISTRY_REST' | 'UPSTREAM_REGISTRAR';
export type EppAdapter = 'standard-epp' | 'vnnic-epp';
/** EPP adapters, 'none' (REST connection without adapter) or the key of a REST adapter this
 * build provides (listed in IntegrationContext.rest_adapters, e.g. "webnic"). */
export type ConnectionAdapter = EppAdapter | 'none' | (string & {});
/** AVAILABLE: EPP, or a REST adapter this build provides (e.g. "webnic").
 * NOT_AVAILABLE_NEEDS_SPECIFICATION: REST connection without adapter — stored and testable,
 * but the platform cannot send commands until the provider's API specification is filed and an
 * adapter exists (ADR-016 §10). */
export type AdapterStatus = 'AVAILABLE' | 'NOT_AVAILABLE_NEEDS_SPECIFICATION';
export type ConnectionStatus = 'ACTIVE' | 'SUSPENDED' | 'RETIRED';
export type VersionStatus = 'PENDING_APPROVAL' | 'ACTIVE' | 'SUPERSEDED' | 'REJECTED' | 'CANCELLED';
export type CredentialStatus = 'PENDING' | 'ACTIVE' | 'ROTATED' | 'REVOKED';
export type ClosedReason = 'APPROVAL_REJECTED' | 'CANCELLED' | 'REVOKED_BY_OPERATOR';
/** ADAPTER_DEFINED: the provider's adapter defines how client id + secret are used (e.g.
 * exchanged for a short-lived token); only offered for adapters that declare it. */
export type AuthMethod = 'HTTP_BASIC' | 'BEARER_TOKEN' | 'API_KEY_HEADER' | 'OAUTH2_CLIENT_CREDENTIALS' | 'ADAPTER_DEFINED';
export type TrafficClass = 'INTERACTIVE' | 'BACKGROUND' | 'POLL' | 'DROPCATCH';
export type IntegrationProvider = 'VNNIC_REGISTRAR_API' | 'VNNIC_EKYC' | 'VNNIC_HSTM' | 'VNNIC_GTLD_REPORTING' | 'VNNIC_WHOIS' | 'VNPT_SMARTCA' | 'CLOUDFLARE_TURNSTILE';
export type ApplyMode = 'LIVE' | 'RESTART';
export type SettingsSource = 'DATABASE' | 'ENVIRONMENT' | 'DISABLED';

export const TLD_CHANNELS: readonly TldChannel[] = ['DIRECT_EPP', 'REGISTRY_REST', 'UPSTREAM_REGISTRAR'];
export const INTEGRATION_PROVIDERS: readonly IntegrationProvider[] = ['VNNIC_REGISTRAR_API', 'VNNIC_EKYC', 'VNNIC_HSTM', 'VNNIC_GTLD_REPORTING', 'VNNIC_WHOIS', 'VNPT_SMARTCA', 'CLOUDFLARE_TURNSTILE'];
export const EPP_ADAPTERS: readonly EppAdapter[] = ['standard-epp', 'vnnic-epp'];

export interface StaffRef {
  id: string;
  display_name: string;
}

export interface ConnectionRef {
  id: string;
  code: string;
  name: string;
  kind: ConnectionKind;
  protocol: ConnectionProtocol;
  adapter_status: AdapterStatus;
  status: ConnectionStatus;
}

/** Status of one secret field. Never contains the value. */
export interface SecretStatus {
  configured: boolean;
  /** REFERENCE: points to a secret store (env:/file:/vault:); STORED: entered in the Control
   * Center and kept encrypted by the platform. */
  source: 'REFERENCE' | 'STORED' | null;
  /** The reference text (e.g. "vault:kv/registry/x#password"); only for source REFERENCE and
   * only for holders of registry.credential.manage, otherwise null. */
  reference: string | null;
  /** Opaque id of a STORED value (for the destroy action); null otherwise. */
  stored_secret_id: string | null;
  changed_at: ISODateTime | null;
  changed_by: StaffRef | null;
  /** STORED value that was destroyed (the version can no longer be used). */
  destroyed: boolean;
}

/** Write-only secret input. KEEP reuses the value of the base version (the active one unless
 * the endpoint takes base_version_id). REFERENCE never accepts "db:" (use VALUE). */
export type SecretInput = { source: 'KEEP' } | { source: 'REFERENCE'; reference: string } | { source: 'VALUE'; value: string };

export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED' | 'EXECUTED' | 'CANCELLED';
export type ApprovalAction = 'integration.manage' | 'registry.credential.manage';

/** A four-eyes request (identity.approval_requests) linked to an integration change.
 * EXPIRED is derived (PENDING or APPROVED past expires_at). */
export interface ApprovalLink {
  id: string;
  /** Permission the approver (and the person applying) must hold. */
  action: ApprovalAction;
  status: ApprovalStatus;
  requested_by: StaffRef;
  requested_at: ISODateTime;
  expires_at: ISODateTime;
  decided_by: StaffRef | null;
  decided_at: ISODateTime | null;
  decision_reason: string | null;
  /** Computed for the caller: holds `action`, is not the requester, status PENDING. */
  can_decide: boolean;
  /** The caller is the requester and may approve it themself (staff level `approval.self_approve`,
   * integration changes only, current TOTP code required). */
  can_self_approve?: boolean;
  /** Approved by the requester themself (self-approval by staff level). */
  self_approved?: boolean;
  /** Status APPROVED, not expired, caller holds `action`. */
  can_apply: boolean;
  /** Status PENDING or APPROVED; caller is the requester or holds `action`. */
  can_cancel: boolean;
}

export type WarningCode =
  | 'ADAPTER_NOT_AVAILABLE' // REST connection: no automated registration yet
  | 'CONNECTION_NOT_CONFIGURED' // channel target has no enabled, configured settings
  | 'NO_SPONSORING_REGISTRAR' // gTLD routed to a registry connection without mapping
  | 'DOMAINS_REMAIN_ON_PREVIOUS' // channel change: existing domains stay where they are
  | 'CERTIFICATE_EXPIRES_SOON' // < 30 days
  | 'SESSION_BUDGET_HIGH' // vnnic-epp: sessions of one process above VNNIC's account limit
  | 'PLAIN_HTTP' // URL without TLS (accepted outside production)
  | 'PRIVATE_ADDRESS' // non-production target on a private network
  | 'VAULT_NOT_CONFIGURED_HERE' // vault: reference; this server has no Vault backend
  | 'SAME_ADDRESS_ALL_ENVIRONMENTS'; // provider preset is VNNIC's only known address

export interface Warning {
  code: WarningCode;
  field: string | null;
}

/** Result of every change endpoint. */
export interface WriteResult<T> {
  /** APPLIED: in force now (within the reload interval for services).
   * PENDING_APPROVAL: stored as a proposal; a second person must approve, then someone applies. */
  result: 'APPLIED' | 'PENDING_APPROVAL';
  item: T;
  approval: ApprovalLink | null;
  warnings: Warning[];
}

/** One service instance's view of a connection or provider (settings.applied_configs). */
export interface AppliedConfig {
  service: string; // e.g. "vnnic-report-worker"
  instance: string;
  /** `settings.applied_configs.subject`: `provider:…` (former name) or `module:…` (the module instance). */
  subject?: string;
  /** Reported under the provider's former name, from before the services resolved module instances. */
  former_subject?: boolean;
  source: SettingsSource; // ENVIRONMENT = still using environment variables
  version: number | null; // version loaded (DATABASE), null otherwise
  applied_at: ISODateTime;
  checked_at: ISODateTime;
  /** e.g. { code: "RESTART_REQUIRED" | "SECRET_UNAVAILABLE" | "CONFIGURATION_INVALID" | "SETTINGS_UNAVAILABLE", message } */
  last_error: { code: string; message: string } | null;
  up_to_date: boolean; // loaded version == active version
}

export interface TestSummary {
  id: string;
  test_kind: TestKind;
  outcome: 'OK' | 'FAILED';
  finished_at: ISODateTime;
  requested_by: StaffRef;
}

// ---------------------------------------------------------------------------
// §2 Context

export interface TrafficClassBudget {
  sessions: number;
  requests_per_second: number;
  burst: number;
}

/** A registry root / intermediate CA configured for server authentication. */
export interface CaCertificate {
  subject: string;
  issuer: string;
  self_signed: boolean;
  not_before: ISODateTime;
  not_after: ISODateTime;
  days_remaining: number;
  sha256: string;
}

/** A certificate an untrusted server presented (public data). */
export interface PresentedCertificate {
  subject: string;
  issuer: string;
  not_before: ISODateTime;
  not_after: ISODateTime;
  sha256: string;
  self_signed: boolean;
  pem: string;
}

export type TlsProblem = 'UNKNOWN_ISSUER' | 'NOT_VALID_FOR_NAME' | 'EXPIRED' | 'NOT_YET_VALID' | 'REVOKED' | 'OTHER';

export interface EppSettings {
  host: string;
  port: number; // 1–65535
  tls: boolean; // must be true on a production deployment
  server_name: string | null; // TLS name if different from host
  ca_pem: string | null; // extra CA certificates (public), PEM
  /** Read-only: the configured CA certificates, described (ignored on write). */
  ca_certificates?: CaCertificate[];
  connect_timeout_ms: number; // default 10000, 1000–60000
  command_timeout_ms: number; // default 30000, 1000–300000
  acquire_timeout_ms: number; // default 5000, 100–60000
  failure_threshold: number; // default 5, 1–100
  open_for_ms: number; // default 30000, 1000–600000
  max_commands_per_session: number | null;
  max_session_idle_ms: number | null;
  max_session_lifetime_ms: number | null;
  /** Session pools per traffic class; empty = one INTERACTIVE session (5 rps, burst 5). */
  classes: Partial<Record<TrafficClass, TrafficClassBudget>>;
}

/** Address of one of VNNIC's systems: a helper that fills the connection's single form
 * ("điền sẵn địa chỉ OT&E / production"), never a second stored configuration. */
export interface EppPreset {
  key: 'VNNIC_EPP_GATEWAY';
  environment: 'PRODUCTION' | 'OTE';
  adapter: 'vnnic-epp';
  settings: EppSettings; // host/port from code constants, TLS on, VNNIC session limits
}

export interface IntegrationContext {
  /** ote: the OT&E (test) server; production: the production server (ADR-018). */
  deployment_environment: 'ote' | 'production';
  /** The deployment's environment (label). */
  integration_environment: IntegrationEnvironment;
  /** Every change needs a second person (production deployment); on the OT&E server changes
   * apply at once. */
  four_eyes: boolean;
  /** "env": the deployment ignores database settings for providers (break-glass). */
  settings_source: 'database' | 'env';
  /** Services pick up changes within this many seconds. */
  refresh_seconds: number;
  approval_ttl_hours: number; // 24
  /** Secret stores available to THIS server (other services may differ). */
  secret_backends: { env: boolean; file: boolean; vault: boolean; db: boolean };
  test_limits: {
    per_target_interval_seconds: number;
    epp_login_per_hour: number;
    epp_login_interval_seconds: number;
    /** OT&E exercise: one start per interval and at most so many per hour, per connection. */
    ote_exercise_interval_seconds: number;
    ote_exercise_per_hour: number;
  };
  epp_presets: EppPreset[];
  /** REST adapters available in this build, e.g.
   * [{ key: "webnic", kinds: ["UPSTREAM_REGISTRAR"], auth_methods: ["ADAPTER_DEFINED"] }].
   * Nothing here says which address this server calls out from (WebNIC's IP allow-list). */
  rest_adapters: { key: string; kinds: ConnectionKind[]; auth_methods: AuthMethod[]; login_test: boolean }[];
  auth_methods: AuthMethod[];
  traffic_classes: TrafficClass[];
}

// ---------------------------------------------------------------------------
// §3 Connections

export interface UpstreamIdentity {
  legal_name: string; // 1–300
  /** ICANN registrar trade name; used as the sponsoring registrar name in VNNIC gTLD reports. */
  trade_name: string; // 1–300
  /** IANA Registrar ID; used as the sponsoring registrar number in VNNIC gTLD reports. */
  iana_id: number; // positive integer
}

export interface CredentialSummary {
  configured: boolean;
  version: number | null;
  source: 'REFERENCE' | 'STORED' | null;
  changed_at: ISODateTime | null;
  pending_version: number | null;
}

export interface CertificateSummary {
  configured: boolean;
  version: number | null;
  not_after: ISODateTime | null;
  days_remaining: number | null;
  pending_version: number | null;
}

export type ReadinessCode =
  | 'ADAPTER_AVAILABLE'
  | 'ENVIRONMENT_ENABLED'
  | 'ENDPOINT_CONFIGURED'
  | 'CREDENTIAL_ACTIVE'
  | 'CERTIFICATE_ACTIVE'
  | 'CERTIFICATE_VALID_30_DAYS'
  | 'CAPABILITIES_IN_FORCE'
  | 'SPONSORING_REGISTRAR_KNOWN';

export interface ReadinessCheck {
  code: ReadinessCode;
  ok: boolean;
  applicable: boolean;
}

/** The connection's settings (one configuration per connection). */
export interface ConnectionEnvironmentSummary {
  enabled: boolean;
  /** "host:port" (EPP) or base URL (REST); null when not configured. */
  endpoint: string | null;
  generation: number;
  /** 0 = configured outside the Control Center (setup script / SQL), no history. */
  active_version: number | null;
  pending_version: number | null;
  credential: CredentialSummary;
  certificate: CertificateSummary | null; // null for REST connections
  last_test: TestSummary | null;
  ready: boolean;
}

export interface ConnectionSummary {
  id: string;
  code: string; // ^[a-z0-9][a-z0-9-]{1,31}$
  name: string;
  kind: ConnectionKind;
  protocol: ConnectionProtocol;
  adapter: ConnectionAdapter;
  adapter_status: AdapterStatus;
  /** What the provider's account API offers (balance, list of the registrar's names). */
  account_features?: AccountFeatures;
  status: ConnectionStatus;
  /**
   * Who runs the RAA duties for domains held here: REGISTRAR_OF_RECORD (Tino: WHOIS accuracy
   * e-mail, own WHOIS privacy) or UPSTREAM_REGISTRAR (e.g. WebNIC verifies and suspends).
   */
  role?: 'REGISTRAR_OF_RECORD' | 'UPSTREAM_REGISTRAR';
  /** Name/version of the provider's API document Tino holds (REST; informational). */
  api_spec_reference: string | null;
  upstream: UpstreamIdentity | null; // only for UPSTREAM_REGISTRAR
  /** The connection's settings; null when nothing is configured yet. */
  settings: ConnectionEnvironmentSummary | null;
  /** TLDs routed here: explicit channel vs. default (zone's default connection). */
  tld_counts: { configured: number; by_default: number };
  domains_held: number; // live domains held through this connection
  in_production_use: boolean; // changes to status/adapter/identity need approval
  open_changes: number;
  version: number; // for PATCH expected_version
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export interface ConnectionDetail extends ConnectionSummary {
  readiness: ReadinessCheck[];
  /** For VNNIC gTLD reporting; null when the connection holds no gTLD. */
  sponsoring_registrar: { iana_id: number; trade_name: string; source: 'UPSTREAM_REGISTRAR' | 'MAPPING' } | null;
  /** Capability version in force (read-only here; maintained outside this page). */
  capabilities: { version: number; effective_from: ISODateTime; flags: Record<string, boolean | number> } | null;
  channels: { tld_id: string; zone: string; source: 'CONFIGURED' | 'DEFAULT' }[];
  open_change_list: IntegrationChange[];
}

export interface ConnectionListQuery {
  kind?: ConnectionKind;
  protocol?: ConnectionProtocol;
  status?: ConnectionStatus;
  /** Code or name, substring. */
  q?: string;
}

export interface CreateConnectionBody {
  code: string;
  name: string; // 1–200
  kind: ConnectionKind;
  protocol: ConnectionProtocol; // UPSTREAM_REGISTRAR requires REST
  /** EPP: required, an EppAdapter. REST: null (no adapter, stored as 'none') or a key from
   * context.rest_adapters whose `kinds` include `kind`. */
  adapter: string | null;
  api_spec_reference: string | null; // ≤ 300
  upstream: UpstreamIdentity | null; // required iff kind = UPSTREAM_REGISTRAR
  reason: string;
}

export interface UpdateConnectionBody {
  expected_version: number;
  name?: string;
  status?: ConnectionStatus;
  /** EPP: another EppAdapter. REST: 'none' or a key from context.rest_adapters. */
  adapter?: string;
  api_spec_reference?: string | null;
  upstream?: UpstreamIdentity; // UPSTREAM_REGISTRAR only (full identity)
  reason: string;
}

// ---------------------------------------------------------------------------
// §4 Connection settings (one configuration per connection)

export interface RestSettings {
  base_url: string; // https:// (http:// only outside production)
  auth_method: AuthMethod;
  /** Non-secret parameters of the auth method. Send only the keys the chosen method uses, with
   * non-empty values: the server rejects an unknown key (UNKNOWN_OPTION) and an empty
   * api_key_header / token_url (OUT_OF_RANGE / INVALID_URL). */
  auth_settings: {
    api_key_header?: string; // API_KEY_HEADER: header name, e.g. "X-Api-Key"
    token_url?: string; // OAUTH2_CLIENT_CREDENTIALS
    scope?: string; // OAUTH2_CLIENT_CREDENTIALS, optional
  };
  /** Account / reseller id at the provider (not secret). */
  account_id: string | null;
  connect_timeout_ms: number; // default 10000, 1000–60000
  request_timeout_ms: number; // default 30000, 1000–300000
}

export interface EnvironmentVersion {
  id: string | null; // null for version 0
  version: number; // 0 = configured outside the Control Center
  status: VersionStatus;
  enabled: boolean;
  epp: EppSettings | null; // EPP connections
  rest: RestSettings | null; // REST connections
  reason: string | null;
  created_by: StaffRef | null;
  created_at: ISODateTime | null;
  activated_by: StaffRef | null;
  activated_at: ISODateTime | null;
  approval: ApprovalLink | null;
}

export interface CredentialVersion {
  version: number;
  status: CredentialStatus;
  /** EPP client ID / username / OAuth client id; null unless the caller holds
   * registry.credential.manage. Empty string when the auth method has none. */
  client_id: string | null;
  secret: SecretStatus; // password / token / API key / client secret
  created_by: StaffRef | null; // null for rows created outside the Control Center
  created_at: ISODateTime;
  activated_by: StaffRef | null;
  activated_at: ISODateTime | null;
  retired_at: ISODateTime | null;
  reason: string | null;
  closed_reason: ClosedReason | null;
  approval: ApprovalLink | null;
}

export interface CertificateVersion {
  version: number;
  status: CredentialStatus;
  subject: string;
  issuer: string;
  serial: string;
  fingerprint_sha256: string; // hex, of the leaf certificate
  not_before: ISODateTime;
  not_after: ISODateTime;
  days_remaining: number;
  certificate_pem: string; // public certificate chain
  private_key: SecretStatus;
  created_by: StaffRef | null;
  created_at: ISODateTime;
  activated_by: StaffRef | null;
  activated_at: ISODateTime | null;
  retired_at: ISODateTime | null;
  reason: string | null;
  closed_reason: ClosedReason | null;
  approval: ApprovalLink | null;
}

export interface EnvironmentDetail {
  connection: ConnectionRef;
  exists: boolean; // false: nothing configured yet (form starts empty or from a preset)
  generation: number | null; // expected_generation for the next proposal
  active: EnvironmentVersion | null;
  pending: EnvironmentVersion | null;
  credentials: CredentialVersion[]; // newest first
  certificates: CertificateVersion[] | null; // null for REST
  readiness: ReadinessCheck[];
  recent_tests: ConnectionTest[]; // latest 5
  applied_by: AppliedConfig[]; // service instances that loaded this connection
}

export interface VersionListQuery {
  before_version?: number;
  limit?: number;
}

export interface EnvironmentVersionList {
  items: EnvironmentVersion[];
  next_before_version: number | null;
}

export interface ProposeEnvironmentBody {
  /** EnvironmentDetail.generation; null when exists = false. */
  expected_generation: number | null;
  enabled: boolean;
  epp: EppSettings | null; // exactly one of epp / rest, matching the protocol
  rest: RestSettings | null;
  reason: string;
}

export interface ProposeCredentialBody {
  expected_active_version: number | null; // version of the ACTIVE credential, null if none
  client_id: string; // EPP: 3–16 chars (clIDType); REST: 0–200
  /** KEEP = same secret as the active credential (only client_id changes). */
  secret: SecretInput;
  reason: string;
}

export interface RevokeBody {
  reason: string;
  /** Must be true to revoke the ACTIVE version (the connection stops working at once). */
  confirm_active: boolean;
}

/** `POST …/certificates/pkcs12`: a PKCS#12 (.p12 / .pfx) file holding the private key, the
 * client certificate and its chain. Needs registry.credential.manage and an MFA-verified session;
 * EPP connections only. Nothing is stored when the file is refused. */
export interface ImportPkcs12Body {
  expected_active_version: number | null;
  /** Standard base64 of the file, at most 64 KiB decoded. */
  file_base64: string;
  /** Write-only; never returned, logged or audited. */
  passphrase: string;
  reason: string;
}

/** What the server read from the imported file. */
export interface Pkcs12Summary {
  subject: string;
  issuer: string;
  not_before: ISODateTime;
  not_after: ISODateTime;
  fingerprint_sha256: string;
  /** Certificates stored, leaf included. */
  chain_length: number;
  /** "RSA-<bits>" | "ECDSA-P256" | "ECDSA-P384" */
  key_type: string;
  /** CA certificates in the file (offered as the registry root CA). */
  ca_certificates?: PresentedCertificate[];
}

export type Pkcs12ImportResult = WriteResult<CertificateVersion> & { summary: Pkcs12Summary };

/** Error codes of the import (422, nothing stored), besides those of POST …/certificates. */
export const PKCS12_ERROR_CODES = [
  'PKCS12_INVALID',
  'PKCS12_TOO_LARGE',
  'PKCS12_BAD_PASSPHRASE',
  'PKCS12_NO_KEY_OR_CERT',
  'PKCS12_UNSUPPORTED_KEY',
  'PKCS12_KEY_TOO_SMALL',
  'CERTIFICATE_EXPIRED',
  'CERTIFICATE_NOT_YET_VALID',
] as const;

/** Largest file the import accepts (decoded). */
export const PKCS12_MAX_BYTES = 64 * 1024;

export interface ProposeCertificateBody {
  expected_active_version: number | null;
  certificate_pem: string; // leaf first, then intermediates; ≤ 65536 chars
  /** KEEP = reuse the active version's key (renewed certificate, same key). */
  private_key: SecretInput;
  /** Password of an encrypted private key typed in; never stored. */
  private_key_password?: string | null;
  reason: string;
}

/** `GET …/connections/{id}/balance` (integration.read): prepaid balance of a WebNIC account
 * (adapter "webnic"). WebNIC is asked at most once per connection per 60 s per server process;
 * answers and errors in between are served from that cache.
 * Errors: 422 NOT_SUPPORTED, 403 FORBIDDEN, 404, 409 NOT_CONFIGURED, 502 AUTH_FAILED
 * (details.hint "WEBNIC_IP_ALLOW_LIST"), 503 UNAVAILABLE; details carry
 * { fetched_at, cached, retry_after_seconds } when WebNIC was (or would have been) asked. */
export interface AccountBalance {
  connection_id: string;
  /** The deployment's environment (label). */
  environment: IntegrationEnvironment;
  /** First balance entry; ISO 4217, upper case. */
  currency: string | null;
  /** Exact decimal string, e.g. "791.1" (never a number). */
  amount: string | null;
  balances: { currency: string; amount: string }[];
  /** When WebNIC was actually asked. */
  fetched_at: ISODateTime;
  cached: boolean;
  next_refresh_after_seconds: number;
}

/** What a provider's account API offers (`registry-core` `account::features_of`). */
export interface AccountFeatures {
  balance: boolean;
  domain_list: boolean;
}

/** Which rows of the name list a page shows. */
export type AccountDomainsFilter = 'all' | 'only_provider' | 'in_both' | 'only_platform';

export interface AccountDomainsQuery {
  /** 1-based. */
  page?: number;
  /** 1..200 (default 50). */
  limit?: number;
  filter?: AccountDomainsFilter;
  /** Part of the name. */
  q?: string;
  /** Ask the provider again ("Làm mới"); honoured once the kept list is older than 60 s. */
  refresh?: boolean;
}

/** One row of the comparison: a name the provider lists (`at_provider`), with the platform's
 * domain of this connection when there is one, or a live name only the platform holds. */
export interface AccountDomainRow {
  name: string;
  created: string | null;
  expires: string | null;
  type: string | null;
  at_provider: boolean;
  platform: { id: string; name: string; lifecycle_state: string; registry_expires_at: ISODateTime | null } | null;
}

/** `GET …/connections/{id}/domains` (integration.read): one page of every name the provider lists
 * for the registrar, compared with the platform's names of this connection (live names the
 * provider does not list included). The provider's list is kept 10 minutes on the server; pages,
 * filters and searches use it. Counts in `summary` cover the whole list; `total` the rows of the
 * filter and search. Errors as the balance. */
export interface AccountDomains {
  connection_id: string;
  environment: IntegrationEnvironment;
  /** When the provider answered (the kept list). */
  fetched_at: ISODateTime;
  /** Served from the kept list. */
  cached: boolean;
  /** "Làm mới" asks the provider again after this many seconds (0: now). */
  next_refresh_after_seconds: number;
  summary: { at_provider: number; in_both: number; only_provider: number; only_platform: number };
  filter: AccountDomainsFilter;
  page: number;
  limit: number;
  total: number;
  items: AccountDomainRow[];
}

/** A name as the registry holds it (`domain:info`; public data). */
export interface RegistryDomainView {
  name: string;
  roid: string | null;
  sponsor: string | null;
  sponsored_by_us: boolean;
  statuses: string[];
  nameservers: string[];
  created_at: ISODateTime | null;
  expires_at: ISODateTime | null;
  /** Registrant contact handle. */
  registrant: string | null;
  contacts: { type: string; handle: string }[];
}

/** The registrant's identity number at the registry against the subjects (blind index). */
export type RegistrantMatch =
  | { status: 'MATCHED'; subject_id: string }
  | { status: 'AMBIGUOUS'; subjects: number }
  | { status: 'NOT_MATCHED' | 'NO_IDENTITY' | 'UNSUPPORTED' }
  | { status: 'UNAVAILABLE'; outcome: string };

/** `GET …/connections/{id}/domains/{name}/registry` (registry.transaction.read): the name at the
 * connection's registry now, read only. Errors: 404 connection, 400 INVALID_DOMAIN, 503
 * REGISTRY_UNAVAILABLE. */
export interface RegistryDomainPreview {
  connection_id: string;
  name: string;
  ascii_name: string;
  read_at: ISODateTime;
  /** null: the registry does not hold the name. */
  registry: RegistryDomainView | null;
  registrant_match: RegistrantMatch;
  /** The matched subject (MATCHED only). */
  subject: { id: string; subject_type: string; hostbill: boolean; managing_customer: { id: string; display_name: string | null } | null } | null;
  /** The platform's live domain of this name. */
  platform: { id: string; lifecycle_state: string } | null;
  importable: boolean;
  /** Why it cannot be brought in (same codes as the import's refusals). */
  refusal: { code: string; message: string } | null;
}

export interface ImportDomainBody {
  customer_id: string;
  /** Left out: the subject the registry's identity number matches. */
  subject_id?: string | null;
  reason: string;
}

/** How an imported domain's subject was found: chosen by staff, the registry's identity number,
 * the subject recorded for the same contact handle, or made from the registry's data. */
export type ImportSubjectSource = 'STAFF' | 'IDENTITY_MATCH' | 'KNOWN_HANDLE' | 'CREATED';

/** `POST …/connections/{id}/domains/{name}/import` (domain.write on the customer). Refusals: 409
 * ALREADY_IN_SYSTEM (details.domain_id), NOT_AT_REGISTRY, NOT_SPONSORED, REGISTRY_STATE_NOT_IMPORTABLE,
 * SUBJECT_NOT_ACTIVE, SUBJECT_OF_ANOTHER_CUSTOMER, CUSTOMER_NOT_ACTIVE; 422 TLD_NOT_CONFIGURED,
 * REGISTRANT_NOT_MATCHED, REGISTRANT_AMBIGUOUS; 404 SUBJECT_NOT_FOUND; 503 REGISTRY_UNAVAILABLE.
 * The domain's contacts keep the registry's handles; subjects no customer managed are given to the
 * customer (`subjects_assigned`), missing ones made from the registry's data (`subjects_created`). */
export interface ImportedDomain {
  connection_id: string;
  domain: {
    domain_id: string;
    name: string;
    lifecycle_state: string;
    subject_id: string;
    subject_source: ImportSubjectSource;
    dossier_id: string;
    verification_id: string | null;
    contacts: { role: 'REGISTRANT' | 'ADMIN' | 'TECH' | 'BILLING'; handle: string; subject_id: string; subject_version_id: string; source: ImportSubjectSource }[];
    subjects_created: string[];
    subjects_assigned: string[];
  };
}

export interface ImportAllBody {
  customer_id: string;
  reason: string;
}

/** One skipped or failed name of a bulk import. */
export interface ImportAllIssue {
  name: string;
  outcome: 'SKIPPED' | 'FAILED';
  code: string;
  message: string;
}

/** A bulk import ("Đưa tất cả vào hệ thống"): a background workflow of the server. */
export interface ImportAllRun {
  id: string;
  /** Workflow state: PENDING / RUNNING / WAITING / RETRYING are still running. */
  state: 'PENDING' | 'RUNNING' | 'WAITING' | 'RETRYING' | 'COMPLETED' | 'FAILED' | 'MANUAL_REVIEW' | 'CANCELLED';
  customer: { id: string | null; display_name: string | null };
  reason: string | null;
  total: number;
  done: number;
  remaining: number;
  imported: number;
  /** Names not taken, by refusal code. */
  skipped: Record<string, number>;
  /** Names that failed, by code (a new run takes them again). */
  failed: Record<string, number>;
  /** The latest 500, without ALREADY_IN_SYSTEM. */
  issues: ImportAllIssue[];
  started_at: ISODateTime;
  completed_at: ISODateTime | null;
  next_run_at: ISODateTime | null;
  failure: { code: string; message: string | null } | null;
}

/** `GET` / `POST …/connections/{id}/domains/import-all`. POST (domain.write on the customer):
 * 409 IMPORT_ALL_RUNNING (details.workflow_id), 422 NOTHING_TO_IMPORT, CUSTOMER_NOT_ACTIVE, and
 * the errors of the name list. GET: registry.transaction.read. */
export interface ImportAllStatus {
  connection_id: string;
  run: ImportAllRun | null;
}

/** `details` of a balance error. */
export interface BalanceErrorDetails {
  fetched_at?: ISODateTime;
  cached?: boolean;
  retry_after_seconds?: number;
  hint?: 'WEBNIC_IP_ALLOW_LIST' | 'VNNIC_REGISTERED_IP';
  error_class?: string;
}

// ---------------------------------------------------------------------------
// §5 TLD channels

export type TldClass = 'VN' | 'GTLD' | 'NEW_GTLD' | 'CCTLD' | 'OTHER';

export interface TldChannelState {
  tld_id: string;
  channel: TldChannel;
  connection: ConnectionRef;
  /** CONFIGURED: set in the Control Center. DEFAULT: no setting, the zone's default connection. */
  source: 'CONFIGURED' | 'DEFAULT';
  version: number | null; // null for DEFAULT
  activated_at: ISODateTime | null;
  activated_by: StaffRef | null;
  /** The connection's settings are enabled and configured, with an active credential. */
  connection_ready: boolean;
  pending: { version: number; channel: TldChannel; connection: ConnectionRef; approval: ApprovalLink } | null;
}

export interface TldChannelRow {
  tld_id: string;
  zone: string; // A-label, e.g. "com", "id.vn", "xn--p1ai"
  unicode_zone: string;
  tld_class: TldClass;
  icann_governed: boolean;
  status: 'ACTIVE' | 'SUSPENDED' | 'RETIRED';
  default_connection: ConnectionRef;
  /** The channel in force (one per TLD). */
  current: TldChannelState;
  /** Live domains of this zone per connection holding them. */
  domains_by_connection: { connection: ConnectionRef; domains: number }[];
}

export interface TldChannelVersion {
  id: string;
  version: number;
  status: VersionStatus;
  channel: TldChannel;
  connection: ConnectionRef;
  batch_id: string | null;
  reason: string;
  created_by: StaffRef;
  created_at: ISODateTime;
  activated_by: StaffRef | null;
  activated_at: ISODateTime | null;
  approval: ApprovalLink | null;
}

export type TldChannelDetail = TldChannelRow & { history: TldChannelVersion[] };

export interface TldChannelQuery {
  channel?: TldChannel;
  connection_id?: string;
  source?: 'CONFIGURED' | 'DEFAULT';
  tld_class?: TldClass;
  /** Zone prefix, U-label or A-label. */
  q?: string;
  /** Exactly this TLD (the TLD page). */
  tld_id?: string;
  /** Zone cursor. */
  after?: string;
  limit?: number;
}

export interface TldChannelList {
  items: TldChannelRow[];
  next_after: string | null;
}

export interface ProposeChannelBody {
  connection_id: string;
  /** Version of the ACTIVE channel; null when the TLD uses its default connection. */
  expected_active_version: number | null;
  reason: string;
}

export interface ChannelBatchBody {
  connection_id: string;
  tld_ids: string[]; // 1–500, distinct
  reason: string;
}

export interface ChannelBatchResult {
  result: 'APPLIED' | 'PENDING_APPROVAL';
  batch_id: string;
  items: TldChannelState[]; // TLDs included
  skipped: { tld_id: string; code: 'SAME_CONNECTION' | 'PENDING_CHANGE_EXISTS' | 'TLD_RETIRED' }[];
  approval: ApprovalLink | null;
  warnings: (Warning & { tld_id: string | null })[];
}

// ---------------------------------------------------------------------------
// §6 Provider settings (VNNIC)

export interface ProviderFieldDefinition {
  /** "base_url" or an option key. */
  key: string;
  type: 'url' | 'path' | 'integer' | 'text';
  required_when_enabled: boolean;
  default: string | number | null;
  min?: number;
  max?: number;
  unit?: 'ms' | 'hour_utc' | 'records' | 'attempts';
  /** Shown under "advanced" in the form. */
  advanced: boolean;
}

export interface ProviderSecretDefinition {
  key: string;
  required_when_enabled: boolean;
}

export interface ProviderPreset {
  environment: IntegrationEnvironment;
  base_url: string;
  options: Record<string, string | number>;
  note: 'SAME_ADDRESS_ALL_ENVIRONMENTS' | null;
}

export interface ProviderDefinition {
  provider: IntegrationProvider;
  /** Services that use it (for the "applies to" line). */
  used_by: string[];
  fields: ProviderFieldDefinition[];
  secrets: ProviderSecretDefinition[];
  presets: ProviderPreset[];
  /** When a change reaches the services. */
  apply_modes: { enabled: ApplyMode; settings: ApplyMode };
  has_sponsoring_registrars: boolean;
  /** A sign-in test exists (one read-only call with the stored credentials). */
  login_test: boolean;
}

export interface ProviderCatalog {
  providers: ProviderDefinition[];
}

export interface ProviderSettingVersion {
  id: string;
  version: number;
  status: VersionStatus;
  enabled: boolean;
  base_url: string | null;
  options: Record<string, string | number | null>;
  secrets: Record<string, SecretStatus>; // one entry per secret field of the provider
  reason: string;
  created_by: StaffRef;
  created_at: ISODateTime;
  activated_by: StaffRef | null;
  activated_at: ISODateTime | null;
  approval: ApprovalLink | null;
}

export interface ProviderSettingSummary {
  provider: IntegrationProvider;
  configured_in_database: boolean;
  generation: number | null;
  active: {
    version: number;
    enabled: boolean;
    base_url: string | null;
    activated_at: ISODateTime | null;
    activated_by: StaffRef | null;
  } | null;
  pending: { version: number; approval: ApprovalLink } | null;
  secrets: Record<string, SecretStatus>; // of the active version (all "not configured" if none)
  last_test: TestSummary | null;
  applied_by: AppliedConfig[];
}

export interface ProviderSettingDetail {
  provider: IntegrationProvider;
  configured_in_database: boolean;
  generation: number | null;
  active: ProviderSettingVersion | null;
  pending: ProviderSettingVersion | null;
  recent_tests: ConnectionTest[];
  applied_by: AppliedConfig[];
}

export interface ProviderSettingVersionList {
  items: ProviderSettingVersion[];
  next_before_version: number | null;
}

export interface ProposeProviderSettingBody {
  expected_generation: number | null; // null when configured_in_database = false
  /** Version whose secrets KEEP reuses (e.g. restoring an older version, or re-proposing a
   * cancelled one). Default: the active version. */
  base_version_id: string | null;
  enabled: boolean;
  base_url: string | null; // required when enabled
  /** Keys from the catalog; missing or null = the catalog default (an empty path is INVALID_PATH,
   * so send null to fall back to the default). */
  options: Record<string, string | number | null>;
  /** Every secret field of the provider must be present. KEEP with nothing to keep is accepted
   * only while the service stays switched off (otherwise NOTHING_TO_KEEP). */
  secrets: Record<string, SecretInput>;
  reason: string;
}

export interface ProviderTestBody {
  /** REACHABILITY (default) or LOGIN (API_LOGIN; providers with `login_test`, address in force only). */
  mode?: 'REACHABILITY' | 'LOGIN';
  /** null = active version. */
  version_id: string | null;
}

export interface SponsoringRegistrar {
  connection: ConnectionRef;
  source: 'UPSTREAM_REGISTRAR' | 'MAPPING' | 'MISSING';
  iana_id: number | null;
  trade_name: string | null;
  /** gTLD / new gTLD zones routed to this connection in PRODUCTION (configured or default). */
  gtld_zones: number;
  gtld_domains_held: number;
  /** false for UPSTREAM_REGISTRAR connections (edit the connection instead). */
  editable: boolean;
  pending: { iana_id: number; trade_name: string; approval: ApprovalLink } | null;
  updated_at: ISODateTime | null;
}

export interface SponsoringRegistrarBody {
  iana_id: number;
  trade_name: string; // 1–300
  expected_updated_at: ISODateTime | null; // null when source = MISSING
  reason: string;
}

// ---------------------------------------------------------------------------
// §7 Stored secrets

export interface DestroySecretResult {
  stored_secret_id: string;
  destroyed_at: ISODateTime;
}

// ---------------------------------------------------------------------------
// §8 Changes waiting for approval

export type ChangeKind =
  | 'CONNECTION_UPDATE'
  | 'ENVIRONMENT_VERSION'
  | 'CREDENTIAL_VERSION'
  | 'CERTIFICATE_VERSION'
  | 'TLD_CHANNEL'
  | 'TLD_CHANNEL_BATCH'
  | 'PROVIDER_SETTING_VERSION'
  | 'SPONSORING_REGISTRAR'
  | 'MODULE_INSTANCE_VERSION';

/** Non-secret fields show before/after; secret fields only that they were replaced (or, for a
 * provider version restored from an older one, removed). Fields that name a connection
 * (`connection`, `connection_id`) carry its id, not its name. Secret fields of provider settings
 * are named `secrets.<key>`. */
export type FieldChange =
  /** hidden: the value is withheld from this reader (the credential's `client_id` for readers
   * without registry.credential.manage); before / after are then null. */
  | { field: string; kind: 'VALUE'; before: unknown; after: unknown; hidden?: boolean }
  | { field: string; kind: 'SECRET'; change: 'REPLACED'; source: 'REFERENCE' | 'STORED' }
  | { field: string; kind: 'SECRET'; change: 'REMOVED'; source: null };

export interface IntegrationChange {
  approval: ApprovalLink;
  kind: ChangeKind;
  /** The deployment's environment when proposed (label); null for connection / sponsoring changes. */
  environment: IntegrationEnvironment | null;
  target: {
    connection: ConnectionRef | null;
    tld: { id: string; zone: string } | null;
    tld_count: number | null; // TLD_CHANNEL_BATCH
    /** TLD_CHANNEL_BATCH: the zones included; null for other kinds. */
    tlds: { id: string; zone: string }[] | null;
    provider: IntegrationProvider | null;
    registry_code: string | null; // SPONSORING_REGISTRAR
    /** MODULE_INSTANCE_VERSION: the module instance (ADR-025 §6.1). */
    module?: { branch: string; module: string; instance_key: string; display_name: string | null } | null;
    version: number | null; // proposed version number
  };
  changes: FieldChange[];
  reason: string;
  /** The target changed after the proposal; it can no longer be applied (cancel and propose again). */
  stale: boolean;
}

export interface ChangeListQuery {
  /** OPEN (default) = approval pending or approved and not expired. */
  state?: 'OPEN' | 'CLOSED' | 'ALL';
  kind?: ChangeKind;
  /** ISO time cursor on requested_at. */
  before?: ISODateTime;
  limit?: number;
}

export interface ChangeList {
  items: IntegrationChange[];
  next_before: ISODateTime | null;
}

/** Existing `POST /approvals/{id}/decision` body. */
export interface ChangeDecisionBody {
  approve: boolean;
  reason: string;
  /** Current authenticator code: required to decide one's own change (self-approval). */
  totp?: string;
}

export interface ChangeDecisionResult {
  id: string;
  status: 'APPROVED' | 'REJECTED';
}

export interface ApplyChangeBody {
  /** Optional note. */
  reason: string | null;
}

export interface ApplyResult {
  result: 'APPLIED';
  change: IntegrationChange; // approval.status = EXECUTED
  item:
    | ConnectionDetail
    | EnvironmentVersion
    | CredentialVersion
    | CertificateVersion
    | TldChannelState
    | TldChannelState[]
    | ProviderSettingVersion
    | SponsoringRegistrar;
}

// ---------------------------------------------------------------------------
// §9 Connection tests

/** API_LOGIN: sign-in test of a REST connection / VNNIC service with the stored credentials
 * (token request when the provider issues one, then one read-only call). */
export type TestKind = 'EPP_HANDSHAKE' | 'EPP_LOGIN' | 'HTTPS_REACHABILITY' | 'API_LOGIN';
/** TOKEN / API_CALL: steps of an API_LOGIN test. */
export type TestStepName = 'DNS' | 'TCP' | 'TLS' | 'GREETING' | 'LOGIN' | 'LOGOUT' | 'HTTP' | 'TOKEN' | 'API_CALL';
export type TestStepError =
  | 'DNS_NOT_FOUND'
  | 'ADDRESS_NOT_ALLOWED'
  | 'CONNECT_REFUSED'
  | 'CONNECT_TIMEOUT'
  | 'TLS_HANDSHAKE_FAILED'
  | 'TLS_CERTIFICATE_INVALID'
  | 'CLIENT_CERTIFICATE_REJECTED'
  | 'GREETING_TIMEOUT'
  | 'GREETING_INVALID'
  | 'LOGIN_REJECTED'
  | 'LOGIN_TIMEOUT'
  | 'HTTP_TIMEOUT'
  | 'HTTP_INVALID_RESPONSE' // the endpoint answered something that is not an HTTP status line
  | 'SECRET_UNAVAILABLE'
  | 'CONFIGURATION_INVALID'
  // API_LOGIN
  | 'WRONG_CREDENTIALS' // the provider said the username / password is wrong
  | 'IP_NOT_ALLOWED' // the provider said this server's address is not allowed
  | 'CREDENTIALS_REJECTED' // refused without saying which of the two
  | 'API_REJECTED' // the read itself was refused
  | 'API_TIMEOUT'
  | 'API_UNREACHABLE'
  | 'API_UNEXPECTED_RESPONSE';

export interface TestStep {
  step: TestStepName;
  status: 'OK' | 'FAILED' | 'SKIPPED';
  duration_ms: number | null;
  error_code: TestStepError | null;
  detail: {
    addresses?: string[]; // DNS
    via_proxy?: boolean; // DNS resolved by the outbound proxy (step SKIPPED); never set by the current server
    address?: string; // TCP
    tls_version?: string; // TLS
    server_certificate?: { subject: string; issuer: string; not_after: ISODateTime; days_remaining: number };
    tls_problem?: TlsProblem;
    server_chain?: PresentedCertificate[];
    client_certificate_version?: number | null;
    server_id?: string; // GREETING (EPP svID)
    server_date?: ISODateTime | null;
    object_uris?: string[];
    extension_uris?: string[];
    /** Extensions the adapter will announce at login that the server does not offer. */
    missing_extensions?: string[];
    result_code?: number; // LOGIN / LOGOUT: EPP result code, e.g. 1000, 2200
    message?: string; // registry's result message (no credentials)
    http_status?: number; // HTTP
    interpretation?: 'REACHABLE' | 'REACHABLE_AUTH_REQUIRED' | 'REACHABLE_REDIRECT' | 'REACHABLE_SERVER_ERROR';
    /** TOKEN / API_CALL: the provider call, e.g. "GET /reseller/v2/balance" (never a query or body). */
    endpoint?: string;
    /** TOKEN / API_CALL: the provider's result code, e.g. "1000" or "2400 DOM4000". */
    provider_code?: string;
    /** API_CALL of an account read: balance per currency (exact decimal strings). */
    balances?: { currency: string; amount: string }[];
    /** API_CALL of a catalogue read: number of entries. */
    item_count?: number;
    /** A refusal that may come from the provider's IP allow-list: this server's public addresses, when declared. */
    egress_addresses?: string[];
  };
}

export interface ConnectionTest {
  id: string;
  test_kind: TestKind;
  outcome: 'OK' | 'FAILED';
  target:
    | { kind: 'REGISTRY_ENVIRONMENT'; connection: ConnectionRef; environment: IntegrationEnvironment }
    | { kind: 'PROVIDER_SETTING'; provider: IntegrationProvider; environment: IntegrationEnvironment };
  tested_version: number | null; // environment / provider version (0 = legacy row)
  credential_version: number | null;
  certificate_version: number | null;
  steps: TestStep[];
  started_at: ISODateTime;
  finished_at: ISODateTime;
  duration_ms: number;
  requested_by: StaffRef;
  /** Where the check ran from; services may use a different network address. */
  probe: { service: string; instance: string };
}

export interface ConnectionTestBody {
  /** HANDSHAKE: connection, encryption and server greeting (EPP).
   * LOGIN: HANDSHAKE + sign in with the stored account and sign out (EPP; uses a registry session).
   * Only against the endpoint in force (409 LOGIN_NEEDS_ENDPOINT_IN_FORCE otherwise).
   * REACHABILITY: REST connections.
   * LOGIN on a REST connection whose adapter has `login_test` (context.rest_adapters): API_LOGIN —
   * a token with the stored credential and one read-only call (WebNIC: Get Account Balance). */
  mode: 'HANDSHAKE' | 'LOGIN' | 'REACHABILITY';
  version_id: string | null; // settings version to test; null = active
  credential_version: number | null; // LOGIN; null = active (or the pending one to pre-test it)
  certificate_version: number | null; // EPP with TLS; null = active
}

export interface ConnectionTestQuery {
  connection_id?: string;
  provider?: IntegrationProvider;
  before?: ISODateTime;
  limit?: number;
}

export interface ConnectionTestList {
  items: ConnectionTest[];
  next_before: ISODateTime | null;
}

// ---------------------------------------------------------------------------
// §9.1 OT&E exercise (every domain operation once against a connection; OT&E server only)

/** Run order. DELETE is opt-in. */
export const EXERCISE_STEPS = ['CHECK', 'CONTACTS', 'CREATE', 'INFO', 'NAMESERVERS', 'LOCK', 'AUTH_INFO', 'DNSSEC', 'RENEW', 'REGISTRANT_CHANGE', 'TRANSFER', 'POLL', 'DELETE'] as const;
export type ExerciseStep = (typeof EXERCISE_STEPS)[number];
/** What each step needs (mirrors `ExerciseStep::requires` in registry-core). */
export const EXERCISE_STEP_REQUIRES: Record<ExerciseStep, readonly ExerciseStep[]> = {
  CHECK: [],
  CONTACTS: [],
  CREATE: ['CHECK', 'CONTACTS'],
  INFO: ['CREATE'],
  NAMESERVERS: ['CREATE'],
  LOCK: ['CREATE'],
  AUTH_INFO: ['CREATE'],
  DNSSEC: ['CREATE'],
  RENEW: ['CREATE'],
  REGISTRANT_CHANGE: ['CREATE'],
  TRANSFER: ['CREATE'],
  POLL: [],
  DELETE: ['CREATE'],
};
export type ExerciseStepStatus = 'PASSED' | 'FAILED' | 'UNKNOWN' | 'SKIPPED';
export type ExerciseStepNote =
  | 'BLOCKED'
  | 'NOT_SUPPORTED'
  | 'NAME_NOT_AVAILABLE'
  | 'REJECTED'
  | 'OUTCOME_UNKNOWN'
  | 'NOT_SENT'
  | 'UNEXPECTED_RESULT'
  | 'MISMATCH'
  | 'NO_MESSAGE'
  | 'STEP_TIMEOUT';

export interface ExerciseCommand {
  command: string; // e.g. "domain:create", "domain:update ns"
  cl_trid: string;
  sv_trid: string | null;
  result_code: number | null;
  message: string; // registry message or transport error; generated codes masked
  outcome: 'SUCCESS' | 'REJECTED' | 'UNKNOWN' | 'NOT_SENT';
  duration_ms: number;
}

export interface ExerciseStepResult {
  step: ExerciseStep;
  status: ExerciseStepStatus;
  note: ExerciseStepNote | null;
  blocked_by: ExerciseStep | null;
  commands: ExerciseCommand[];
  /** Non-secret observations: statuses, expiry dates, name servers, contact handles, result codes. */
  facts: Record<string, unknown> | null;
  started_at: ISODateTime;
  duration_ms: number;
}

export interface ExerciseRun {
  id: string;
  connection_id: string;
  environment: 'OTE';
  /** ABANDONED: the admin-api running it stopped (restart, deploy) before the end. */
  status: 'RUNNING' | 'PASSED' | 'FAILED' | 'ABANDONED';
  domain_name: string;
  selected_steps: ExerciseStep[];
  steps: ExerciseStepResult[];
  requested_by: StaffRef;
  started_at: ISODateTime;
  finished_at: ISODateTime | null;
  duration_ms: number | null;
  probe: { service: string; instance: string };
}

export interface StartExerciseBody {
  /** Absent: every step but DELETE. A step needs its EXERCISE_STEP_REQUIRES (400 otherwise). */
  steps?: ExerciseStep[];
}

// ---------------------------------------------------------------------------
// §10 History

export type HistoryResourceType =
  | 'registry_connection'
  | 'tld'
  | 'tld_channel_batch'
  | 'provider_setting'
  | 'gtld_sponsoring_registrar'
  | 'integration_secret';

export interface IntegrationHistoryEntry {
  id: string;
  at: ISODateTime;
  actor: { type: 'OPERATOR'; user: StaffRef } | { type: 'SYSTEM' | 'EXTERNAL'; id: string };
  /** integration.connection.create|update, integration.environment.propose|activate,
   * integration.credential.propose|activate|revoke, integration.certificate.propose|activate|revoke,
   * integration.certificate.import_pkcs12 (refused import), integration.balance_read,
   * integration.tld_channel.propose|activate, integration.provider_setting.propose|activate,
   * integration.sponsoring_registrar.propose|activate, integration.secret.store|destroy,
   * integration.change.cancel, integration.connection_test, approval.request, approval.decide */
  action: string;
  outcome: 'SUCCESS' | 'DENIED' | 'FAILED';
  resource_type: HistoryResourceType;
  resource_id: string;
  environment: IntegrationEnvironment | null;
  changes: FieldChange[];
  approval_request_id: string | null;
  reason: string | null;
  request_id: string | null;
  correlation_id: string | null;
}

export interface HistoryQuery {
  resource_type?: HistoryResourceType;
  resource_id?: string;
  environment?: IntegrationEnvironment;
  /** Prefix, e.g. "integration.credential". */
  action?: string;
  actor_id?: string;
  before?: ISODateTime;
  limit?: number;
}

export interface HistoryList {
  items: IntegrationHistoryEntry[];
  next_before: ISODateTime | null;
}

// ---------------------------------------------------------------------------
// Methods (paths in integrations.http.ts; all relative to /admin/v1)

export interface IntegrationsApi {
  // §2
  getIntegrationContext(opts?: CallOptions): Promise<IntegrationContext>;
  // §3
  listConnections(q: ConnectionListQuery, opts?: CallOptions): Promise<ItemList<ConnectionSummary>>;
  createConnection(body: CreateConnectionBody, opts?: WriteOptions): Promise<WriteResult<ConnectionDetail>>;
  getConnection(id: string, opts?: CallOptions): Promise<ConnectionDetail>;
  updateConnection(id: string, body: UpdateConnectionBody, opts?: WriteOptions): Promise<WriteResult<ConnectionDetail>>;
  /** `PUT /integrations/connections/{id}/role` (audited with the reason). */
  setConnectionRole(id: string, body: { role: 'REGISTRAR_OF_RECORD' | 'UPSTREAM_REGISTRAR'; reason: string }, opts?: WriteOptions): Promise<{ id: string; role: string; changed: boolean }>;
  // §4
  getConnectionEnvironment(id: string, opts?: CallOptions): Promise<EnvironmentDetail>;
  listEnvironmentVersions(id: string, q: VersionListQuery, opts?: CallOptions): Promise<EnvironmentVersionList>;
  proposeEnvironmentVersion(id: string, body: ProposeEnvironmentBody, opts?: WriteOptions): Promise<WriteResult<EnvironmentVersion>>;
  listCredentialVersions(id: string, opts?: CallOptions): Promise<ItemList<CredentialVersion>>;
  proposeCredential(id: string, body: ProposeCredentialBody, opts?: WriteOptions): Promise<WriteResult<CredentialVersion>>;
  revokeCredential(id: string, version: number, body: RevokeBody, opts?: WriteOptions): Promise<{ item: CredentialVersion }>;
  listCertificateVersions(id: string, opts?: CallOptions): Promise<ItemList<CertificateVersion>>;
  proposeCertificate(id: string, body: ProposeCertificateBody, opts?: WriteOptions): Promise<WriteResult<CertificateVersion>>;
  importCertificatePkcs12(id: string, body: ImportPkcs12Body, opts?: WriteOptions): Promise<Pkcs12ImportResult>;
  revokeCertificate(id: string, version: number, body: RevokeBody, opts?: WriteOptions): Promise<{ item: CertificateVersion }>;
  testConnection(id: string, body: ConnectionTestBody, opts?: WriteOptions): Promise<ConnectionTest>;
  getAccountBalance(id: string, opts?: CallOptions): Promise<AccountBalance>;
  getAccountDomains(id: string, q: AccountDomainsQuery, opts?: CallOptions): Promise<AccountDomains>;
  previewRegistryDomain(id: string, name: string, opts?: CallOptions): Promise<RegistryDomainPreview>;
  importRegistryDomain(id: string, name: string, body: ImportDomainBody, opts?: WriteOptions): Promise<ImportedDomain>;
  startImportAll(id: string, body: ImportAllBody, opts?: WriteOptions): Promise<ImportAllStatus>;
  getImportAll(id: string, opts?: CallOptions): Promise<ImportAllStatus>;
  /** OT&E server only (403 EXERCISE_OTE_ONLY on a production deployment); 202 with the RUNNING run; 409 EXERCISE_IN_PROGRESS, 429 EXERCISE_RATE_LIMITED. */
  startOteExercise(id: string, body: StartExerciseBody, opts?: WriteOptions): Promise<ExerciseRun>;
  listOteExercises(id: string, q: { limit?: number }, opts?: CallOptions): Promise<ItemList<ExerciseRun>>;
  getOteExercise(id: string, runId: string, opts?: CallOptions): Promise<ExerciseRun>;
  // §5
  listTldChannels(q: TldChannelQuery, opts?: CallOptions): Promise<TldChannelList>;
  getTldChannel(tldId: string, opts?: CallOptions): Promise<TldChannelDetail>;
  proposeTldChannel(tldId: string, body: ProposeChannelBody, opts?: WriteOptions): Promise<WriteResult<TldChannelState>>;
  proposeTldChannelBatch(body: ChannelBatchBody, opts?: WriteOptions): Promise<ChannelBatchResult>;
  // §6
  getProviderCatalog(opts?: CallOptions): Promise<ProviderCatalog>;
  listProviderSettings(opts?: CallOptions): Promise<ItemList<ProviderSettingSummary>>;
  getProviderSetting(provider: IntegrationProvider, opts?: CallOptions): Promise<ProviderSettingDetail>;
  listProviderSettingVersions(provider: IntegrationProvider, q: VersionListQuery, opts?: CallOptions): Promise<ProviderSettingVersionList>;
  proposeProviderSetting(provider: IntegrationProvider, body: ProposeProviderSettingBody, opts?: WriteOptions): Promise<WriteResult<ProviderSettingVersion>>;
  testProviderSetting(provider: IntegrationProvider, body: ProviderTestBody, opts?: WriteOptions): Promise<ConnectionTest>;
  listSponsoringRegistrars(opts?: CallOptions): Promise<ItemList<SponsoringRegistrar>>;
  proposeSponsoringRegistrar(registryCode: string, body: SponsoringRegistrarBody, opts?: WriteOptions): Promise<WriteResult<SponsoringRegistrar>>;
  // §7
  destroyStoredSecret(storedSecretId: string, body: { reason: string }, opts?: WriteOptions): Promise<DestroySecretResult>;
  // §8
  listIntegrationChanges(q: ChangeListQuery, opts?: CallOptions): Promise<ChangeList>;
  getIntegrationChange(approvalId: string, opts?: CallOptions): Promise<IntegrationChange>;
  /** The existing approvals endpoint (`POST /approvals/{id}/decision`), typed for integration changes. */
  decideIntegrationChange(approvalId: string, body: ChangeDecisionBody, opts?: WriteOptions): Promise<ChangeDecisionResult>;
  applyIntegrationChange(approvalId: string, body: ApplyChangeBody, opts?: WriteOptions): Promise<ApplyResult>;
  cancelIntegrationChange(approvalId: string, body: { reason: string }, opts?: WriteOptions): Promise<{ change: IntegrationChange }>;
  // §9
  listConnectionTests(q: ConnectionTestQuery, opts?: CallOptions): Promise<ConnectionTestList>;
  // §10
  listIntegrationHistory(q: HistoryQuery, opts?: CallOptions): Promise<HistoryList>;
}
